import { atom } from 'jotai';

import { DeviceTrendPoints } from '@repo/sdk/models/components';

import {
  SelectionsDeviceGroup,
  SidebarAccordionItem,
} from '@/features/trends/components/selections-sidebar/selections.types';
import {
  filterDeviceBySearchTerm,
  groupDevicesByKind,
  transformGroupedDevicesToAccordion,
} from '@/features/trends/components/selections-sidebar/selections.utils';
import {
  chartExpandedSectionsAtom,
  chartSearchTermAtom,
  chartShowAdvancedAtom,
} from '@/features/trends/state/chart.state';
import { currentChartIdAtom } from '@/features/trends/state/current-chart.state';
import { DeviceAttribute } from '@/features/trends/trends.types';

const SelectionState = {
  /**
   * Derived atom that provides read/write access to search term in the current chart's selection.
   * Returns an empty string if no selection exists. Updates propagate to the parent selection.
   *
   * This delegates to chartSearchTermAtom with the current chart ID.
   *
   * Update Cascade:
   * Updating the current chart search term
   *  -> updates the current chart selection atom
   *    -> updates the current chart atom
   *      -> updates the trend group atom
   */
  searchTerm: atom(
    (get): string => {
      const currentChartId = get(currentChartIdAtom);
      if (!currentChartId) return '';

      return get(chartSearchTermAtom(currentChartId));
    },
    (get, set, updated: string) => {
      const currentChartId = get(currentChartIdAtom);
      if (!currentChartId) return;

      set(chartSearchTermAtom(currentChartId), updated);
    }
  ),

  /**
   * Derived atom that provides read/write access to expanded sections in the current chart's selection.
   * Returns an empty array if no selection exists. Updates propagate to the parent selection.
   *
   * This delegates to chartExpandedSectionsAtom with the current chart ID.
   * When searching, returns the stored expanded sections (which get auto-populated by useAutoExpandSearch).
   *
   * Update Cascade:
   * Updating the current chart expanded sections
   *  -> updates the current chart selection atom
   *    -> updates the current chart atom
   *      -> updates the trend group atom
   */
  expandedSectionIds: atom(
    (get): string[] => {
      const currentChartId = get(currentChartIdAtom);

      if (!currentChartId) return [];

      return get(chartExpandedSectionsAtom(currentChartId));
    },
    (get, set, updated: string[]) => {
      const currentChartId = get(currentChartIdAtom);
      if (!currentChartId) return;

      set(chartExpandedSectionsAtom(currentChartId), updated);
    }
  ),

  /**
   * Derived atom that provides read/write access to advanced mode for the current chart's selection.
   * Delegates to chartShowAdvancedAtom with the current chart ID (persisted with trend groups).
   */
  showAdvanced: atom(
    (get): boolean => {
      const currentChartId = get(currentChartIdAtom);
      if (!currentChartId) return false;

      return get(chartShowAdvancedAtom(currentChartId));
    },
    (get, set, updated: boolean) => {
      const currentChartId = get(currentChartIdAtom);
      if (!currentChartId) return;

      set(chartShowAdvancedAtom(currentChartId), updated);
    }
  ),

  /**
   * Derived atom that groups and filters devices based on search term and advanced mode.
   * Accepts device trend points as input and returns a record of device kind to array of matching devices.
   *
   * This is a helper atom that encapsulates the grouping logic.
   * The component should pass deviceTrendPoints from useListDeviceTrendPoints.
   */
  groupedDevices: atom(
    (get) =>
      (
        deviceTrendPoints: DeviceTrendPoints[] | undefined
      ): SidebarAccordionItem<SelectionsDeviceGroup>[] => {
        if (!deviceTrendPoints) return [];

        const searchTerm = get(SelectionState.searchTerm);
        const showAdvanced = get(SelectionState.showAdvanced);

        // Filter devices by search term
        const filteredDevices = deviceTrendPoints
          .map((device) => filterDeviceBySearchTerm(device, searchTerm, showAdvanced))
          .filter((device) => device !== null);

        // Group devices by kind
        const groupedByKind = groupDevicesByKind(filteredDevices);

        // Transform to accordion structure with sorting
        return transformGroupedDevicesToAccordion(groupedByKind, showAdvanced);
      }
  ),

  /**
   * Derived atom that extracts all visible device attributes from the grouped devices.
   * Returns a flat array of DeviceAttribute objects that are currently visible based on search term and advanced mode.
   * This is used by the select all functionality to know which attributes to select/deselect.
   *
   * This is a helper atom that accepts grouped devices as input.
   */
  visibleAttributes: atom(
    () =>
      (groupedDevices: SidebarAccordionItem<SelectionsDeviceGroup>[]): DeviceAttribute[] => {
        const visibleAttrs: DeviceAttribute[] = [];

        groupedDevices.forEach((group) => {
          group.data.devices.forEach((device) => {
            device.data.displayRefs.forEach((displayRef) => {
              visibleAttrs.push({
                deviceId: device.data.deviceId,
                displayRef,
                displayName: device.data.displayName,
              });
            });
          });
        });

        return visibleAttrs;
      }
  ),
};

SelectionState.searchTerm.debugLabel = 'SelectionState.SearchTerm';
SelectionState.expandedSectionIds.debugLabel = 'SelectionState.ExpandedSectionIds';
SelectionState.showAdvanced.debugLabel = 'SelectionState.ShowAdvanced';
SelectionState.groupedDevices.debugLabel = 'SelectionState.GroupedDevices';
SelectionState.visibleAttributes.debugLabel = 'SelectionState.VisibleAttributes';

export default SelectionState;
